import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../../api';
import { ShoppingCart, Users, Package, TrendingUp, ArrowUp, ArrowDown, Eye } from 'lucide-react';
import toast from 'react-hot-toast';

const StatCard = ({ title, value, change, icon: Icon, color, prefix = '' }) => (
  <div style={{ background: 'white', borderRadius: 16, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: 16 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <div style={{ color: '#64748b', fontSize: 13, fontWeight: 500 }}>{title}</div>
        <div style={{ color: '#0f172a', fontSize: 28, fontWeight: 700, marginTop: 4 }}>{prefix}{value?.toLocaleString()}</div>
      </div>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={22} color={color} />
      </div>
    </div>
    {change !== undefined && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {change >= 0
          ? <ArrowUp size={14} color="#22c55e" />
          : <ArrowDown size={14} color="#ef4444" />}
        <span style={{ color: change >= 0 ? '#22c55e' : '#ef4444', fontSize: 13, fontWeight: 600 }}>{Math.abs(change)}%</span>
        <span style={{ color: '#94a3b8', fontSize: 13 }}>vs last month</span>
      </div>
    )}
  </div>
);

const STATUS_COLORS = {
  pending: { bg: '#fef3c7', text: '#d97706' },
  confirmed: { bg: '#dbeafe', text: '#2563eb' },
  shipped: { bg: '#ede9fe', text: '#7c3aed' },
  delivered: { bg: '#dcfce7', text: '#16a34a' },
  cancelled: { bg: '#fee2e2', text: '#dc2626' },
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await adminAPI.getDashboard();
        setStats(res.data.data);
      } catch {
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
      <div className="spinner" />
    </div>
  );

  const { overview = {}, recent_orders = [], top_products = [] } = stats || {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 }}>Dashboard</h1>
        <p style={{ color: '#64748b', marginTop: 4 }}>Welcome back! Here's what's happening.</p>
      </div>

      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
        <StatCard title="Total Revenue" value={overview.total_revenue || 0} prefix="₹" icon={TrendingUp} color="#f43397" change={12} />
        <StatCard title="Total Orders" value={overview.total_orders || 0} icon={ShoppingCart} color="#6c3fc5" change={8} />
        <StatCard title="Total Users" value={overview.total_users || 0} icon={Users} color="#0ea5e9" change={5} />
        <StatCard title="Total Products" value={overview.total_products || 0} icon={Package} color="#f59e0b" change={-2} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20 }}>
        {/* Recent Orders */}
        <div style={{ background: 'white', borderRadius: 16, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 }}>Recent Orders</h2>
            <Link to="/admin/orders" style={{ color: '#f43397', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>View all →</Link>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                  {['Order ID', 'Customer', 'Amount', 'Status', 'Date', ''].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#94a3b8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recent_orders.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>No orders yet</td></tr>
                ) : recent_orders.map(order => {
                  const sc = STATUS_COLORS[order.status] || { bg: '#f1f5f9', text: '#64748b' };
                  return (
                    <tr key={order.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '12px', fontSize: 13, color: '#6c3fc5', fontWeight: 600 }}>#{order.id?.slice(0, 8)}</td>
                      <td style={{ padding: '12px', fontSize: 13, color: '#0f172a' }}>{order.user_name}</td>
                      <td style={{ padding: '12px', fontSize: 13, fontWeight: 600 }}>₹{parseFloat(order.total_amount || 0).toFixed(0)}</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{ background: sc.bg, color: sc.text, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{order.status}</span>
                      </td>
                      <td style={{ padding: '12px', fontSize: 12, color: '#94a3b8' }}>{new Date(order.created_at).toLocaleDateString()}</td>
                      <td style={{ padding: '12px' }}>
                        <Link to={`/admin/orders`} style={{ color: '#64748b' }}><Eye size={16} /></Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Products */}
        <div style={{ background: 'white', borderRadius: 16, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 }}>Top Products</h2>
            <Link to="/admin/products" style={{ color: '#f43397', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>View all →</Link>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {top_products.length === 0 ? (
              <div style={{ color: '#94a3b8', textAlign: 'center', padding: 24 }}>No products yet</div>
            ) : top_products.map((p, i) => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < top_products.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: i === 0 ? '#fef3c7' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: i === 0 ? '#d97706' : '#64748b', flexShrink: 0 }}>
                  {i + 1}
                </div>
                <img src={p.image_url || `https://picsum.photos/seed/${p.id}/40/40`} alt={p.name} style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>{p.total_sold || 0} sold</div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#f43397' }}>₹{parseFloat(p.price || 0).toFixed(0)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Status summary */}
      <div style={{ background: 'white', borderRadius: 16, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '0 0 20px' }}>Order Status Overview</h2>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {Object.entries(STATUS_COLORS).map(([status, colors]) => (
            <div key={status} style={{ padding: '12px 20px', borderRadius: 12, background: colors.bg, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: colors.text, fontWeight: 700, fontSize: 20 }}>
                {overview[`${status}_orders`] || 0}
              </span>
              <span style={{ color: colors.text, fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}>{status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
